import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Logo from '../components/Logo';
import {
  Rocket, CheckCircle, AlertTriangle, User, Lock, Eye, EyeOff,
  ChevronRight, ChevronLeft, Loader2, FileText, Shield, Brain, ScrollText,
  GraduationCap, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = ['parental_consent', 'ferpa_acknowledgment', 'ai_processing_disclosure', 'terms_acceptance'];
const DOC_ICONS = { parental_consent: FileText, ferpa_acknowledgment: ScrollText, ai_processing_disclosure: Brain, terms_acceptance: Shield };

function DocumentViewer({ title, sections, keyTerms, signature, setSignature, onSign, signing }) {
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const checkAtBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.clientHeight < 40 || el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  };
  useEffect(() => {
    setScrolledToBottom(false);
    const id = requestAnimationFrame(checkAtBottom);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, keyTerms]);
  const handleScroll = () => checkAtBottom();
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-white">{title}</h3>
      <p className="text-xs text-slate-300">Read the entire document. Scroll to the bottom to sign.</p>
      <div ref={scrollRef} onScroll={handleScroll}
        className="max-h-[280px] overflow-y-auto bg-[#0a1729] rounded-lg border border-slate-700 p-4 space-y-3 text-sm"
        data-testid="document-scroll-area">
        {sections?.map((sec, i) => (
          <div key={i}>
            <h4 className="font-semibold text-white mb-1 text-sm">{sec.heading}</h4>
            <p className="text-slate-300 whitespace-pre-line leading-relaxed text-xs">{sec.content}</p>
          </div>
        ))}
        {keyTerms?.length > 0 && (
          <div>
            <h4 className="font-semibold text-white mb-1 text-sm">Key Terms</h4>
            <ol className="list-decimal pl-5 space-y-1 text-slate-300 text-xs">
              {keyTerms.map((term, i) => <li key={i}>{term}</li>)}
            </ol>
          </div>
        )}
      </div>
      {!scrolledToBottom && (
        <p className="text-amber-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Scroll to the bottom to enable signing</p>
      )}
      <div className="flex gap-2">
        <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full legal name to sign"
          disabled={!scrolledToBottom} className={`flex-1 ${!scrolledToBottom ? 'opacity-50' : ''}`} data-testid="signature-input" />
        <Button onClick={onSign} disabled={!scrolledToBottom || !signature.trim() || signing} className="shrink-0" data-testid="sign-document-btn">
          {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Sign</>}
        </Button>
      </div>
    </div>
  );
}

export default function AccountSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const sessionId = searchParams.get('session_id') || '';

  // verifying | setup | child-info | child-consent | done | error
  const [step, setStep] = useState('verifying');
  const [regData, setRegData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Account setup
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dob, setDob] = useState('');

  // Auth
  const [authToken, setAuthToken] = useState('');

  // Children
  const [currentChild, setCurrentChild] = useState(0);
  const [childForm, setChildForm] = useState({ firstName: '', lastName: '', dob: '', grade: '9th', email: '' });
  const [registeredChildren, setRegisteredChildren] = useState([]);

  // Consent documents
  const [templates, setTemplates] = useState({});
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [signature, setSignature] = useState('');
  const [signing, setSigning] = useState(false);

  useEffect(() => { verifyPayment(); loadTemplates(); }, []);

  // DOB bounds, prevents 5+ digit years and enforces realistic ranges
  const todayStr = new Date().toISOString().slice(0, 10);
  const ymd = (yearsAgo) => {
    const d = new Date(); d.setFullYear(d.getFullYear() - yearsAgo); return d.toISOString().slice(0, 10);
  };
  // Adult/Professional must be 14+, children must be 14-17
  const adultDobMin = ymd(100);
  const adultDobMax = ymd(14);
  const childDobMin = ymd(17); // earliest allowed = 17 years ago
  const childDobMax = ymd(14); // latest allowed = 14 years ago

  // Validate a YYYY-MM-DD string and return age in years, or null if invalid
  const validateDob = (value) => {
    if (!value || typeof value !== 'string') return { valid: false, reason: 'Date of birth is required' };
    // Force 4-digit year, HTML date input on some browsers accepts 6+ digits
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return { valid: false, reason: 'Date must be in YYYY-MM-DD format with a 4-digit year' };
    const [_, y, mo, d] = m;
    const year = parseInt(y, 10);
    const month = parseInt(mo, 10);
    const day = parseInt(d, 10);
    const now = new Date();
    if (year < 1900 || year > now.getFullYear()) return { valid: false, reason: `Year must be between 1900 and ${now.getFullYear()}` };
    const dt = new Date(`${y}-${mo}-${d}T00:00:00`);
    if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== year || dt.getMonth() + 1 !== month || dt.getDate() !== day) {
      return { valid: false, reason: 'Invalid date' };
    }
    if (dt > now) return { valid: false, reason: 'Date of birth cannot be in the future' };
    const ageMs = now - dt;
    const age = Math.floor(ageMs / (365.25 * 24 * 60 * 60 * 1000));
    return { valid: true, age };
  };

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/compliance/all-templates`);
      const map = {};
      for (const t of res.data.templates) map[t.document_type] = t;
      setTemplates(map);
    } catch (e) { console.error('Failed to load templates:', e); }
  };

  const verifyPayment = async () => {
    if (!token) { setStep('error'); setError('Missing setup token. Please check your email for the correct link.'); return; }
    try {
      const res = await axios.get(`${API}/register/verify-payment?token=${token}&session_id=${sessionId}`);
      if (res.data.success) {
        setRegData(res.data);
        // RESUME mode: parent has already paid AND already created their account.
        // Skip the setup-account step and jump straight to child-info.
        const isResume = new URLSearchParams(window.location.search).get('resume') === '1';
        if (isResume && res.data.role === 'parent') {
          // Make sure we have an auth token; if not, send to login first
          const authToken = localStorage.getItem('futurepath_token');
          if (!authToken) {
            navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            return;
          }
          setStep('child-info');
        } else {
          setStep('setup');
        }
      } else {
        setError(res.data.message || 'Payment not confirmed yet.');
        setStep('error');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to verify payment.');
      setStep('error');
    }
  };

  const handleSetupAccount = async () => {
    setError('');
    if (!fullName.trim()) { setError('Full name is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    // Validate DOB only when collected (non-parent flows)
    if (regData?.role !== 'parent') {
      const dobCheck = validateDob(dob);
      if (!dobCheck.valid) { setError(dobCheck.reason); return; }
      if (dobCheck.age < 14) { setError('You must be at least 14 to use Agentic FuturePath AI.'); return; }
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/register/setup-account`, {
        token, full_name: fullName, password, date_of_birth: dob || null,
      });
      if (res.data.success) {
        setAuthToken(res.data.access_token);
        localStorage.setItem('futurepath_token', res.data.access_token);
        if (regData?.role === 'parent' && regData?.num_children > 0) {
          setStep('child-info');
          toast.success('Account created! Now register your children.');
        } else {
          setStep('done');
          toast.success('Welcome to Agentic FuturePath AI!');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set up account');
    } finally { setLoading(false); }
  };

  const handleChildInfoNext = () => {
    setError('');
    if (!childForm.firstName || !childForm.lastName) { setError('Child\'s name is required'); return; }
    const dobCheck = validateDob(childForm.dob);
    if (!dobCheck.valid) { setError(dobCheck.reason); return; }
    if (!childForm.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(childForm.email)) { setError('Valid email is required for child login'); return; }

    const age = dobCheck.age;
    if (age < 14) { setError(`${childForm.firstName} must be at least 14 years old.`); return; }
    if (age >= 18) { setError(`${childForm.firstName} is 18+. They should register as a Young Adult.`); return; }

    // Move to consent signing for this child
    setCurrentDocIndex(0);
    setSignature('');
    setStep('child-consent');
  };

  const handleSignDoc = async () => {
    const docType = DOC_TYPES[currentDocIndex];
    const childFullName = `${childForm.firstName} ${childForm.lastName}`.trim();
    setSigning(true);
    try {
      await axios.post(`${API}/compliance/sign-document`, {
        document_type: docType,
        typed_signature: signature,
        child_name: childFullName,
        child_dob: childForm.dob,
      }, { headers: { Authorization: `Bearer ${authToken}` } });

      setSignature('');
      toast.success(`Document signed for ${childForm.firstName}`);

      if (currentDocIndex + 1 < DOC_TYPES.length) {
        setCurrentDocIndex(prev => prev + 1);
      } else {
        // All docs signed, create the child account
        await createChildAccount();
      }
    } catch (err) {
      showFriendlyError(err, { fallback: 'Signing failed' });
    } finally { setSigning(false); }
  };

  const createChildAccount = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/register/add-child`, {
        child_first_name: childForm.firstName,
        child_last_name: childForm.lastName,
        child_dob: childForm.dob,
        child_grade_level: childForm.grade,
        child_email: childForm.email,
      }, { headers: { Authorization: `Bearer ${authToken}` } });

      if (res.data.success) {
        const newList = [...registeredChildren, { name: `${childForm.firstName} ${childForm.lastName}`, email: childForm.email }];
        setRegisteredChildren(newList);
        toast.success(`${childForm.firstName}'s account created!`);

        if (newList.length >= regData.num_children) {
          setStep('done');
        } else {
          setCurrentChild(prev => prev + 1);
          setChildForm({ firstName: '', lastName: '', dob: '', grade: '9th', email: '' });
          setStep('child-info');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create child account');
      setStep('child-info');
    } finally { setLoading(false); }
  };

  const getEnhancedSections = (template) => {
    const childFullName = `${childForm.firstName} ${childForm.lastName}`.trim();
    return template?.sections?.map(sec => ({
      ...sec,
      content: sec.content
        .replace(/the student identified during registration[^.]*\.?/gi,
          `${childFullName}, born ${childForm.dob || 'N/A'}, whose parent/guardian ${fullName} provides consent.`)
        .replace(/your child/gi, childFullName)
        .replace(/the child named above/gi, childFullName)
        .replace(/the minor/gi, childFullName)
    })) || [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <Logo size="md" withWordmark />
        </div>

        {/* Verifying */}
        {step === 'verifying' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12" data-testid="verifying-payment">
            <Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-1">Verifying your payment...</h2>
            <p className="text-slate-300 text-sm">This will only take a moment.</p>
          </motion.div>
        )}

        {/* Error */}
        {step === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8" data-testid="setup-error">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-slate-300 text-sm mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={verifyPayment} variant="outline" className="border-slate-700 text-slate-300">Try Again</Button>
              <Button onClick={() => navigate('/pricing')} className="bg-blue-600">Back to plans</Button>
            </div>
          </motion.div>
        )}

        {/* Account Setup */}
        {step === 'setup' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="account-setup-form">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">Payment Confirmed!</h1>
              <p className="text-slate-300 text-sm">Create your account to get started.</p>
              {regData?.plan_name && <p className="text-blue-400 text-xs mt-1">{regData.plan_name}, {regData.email}</p>}
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Full Name</label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-300 absolute left-3 top-3" />
                  <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none" data-testid="setup-name" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Create Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-300 absolute left-3 top-3" />
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-10 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none" data-testid="setup-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-300 hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">Confirm Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-300 absolute left-3 top-3" />
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none" data-testid="setup-confirm-password" />
                </div>
              </div>
              {regData?.role !== 'parent' && (
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Date of Birth</label>
                  <input type="date" value={dob} onChange={e => { setDob(e.target.value); if (error) setError(''); }}
                    min={adultDobMin} max={adultDobMax} required
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none" data-testid="setup-dob" />
                </div>
              )}
              {error && <p className="text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">{error}</p>}
              <Button onClick={handleSetupAccount} disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 py-5 rounded-xl text-sm font-semibold" data-testid="setup-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{regData?.role === 'parent' ? 'Create Account & Register Children' : 'Create My Account'} <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Child Info */}
        {step === 'child-info' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="child-info-step">
            <div className="text-center mb-4">
              <h2 className="text-lg font-bold text-white">Child {currentChild + 1} of {regData?.num_children}</h2>
              <p className="text-slate-300 text-xs mt-0.5">Enter your child's info, then sign consent documents</p>
              <p className="text-amber-400 text-xs mt-1 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> For students ages 14-17 only</p>
            </div>

            {registeredChildren.length > 0 && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <p className="text-emerald-400 text-xs font-medium mb-1">Registered:</p>
                {registeredChildren.map((c, i) => (
                  <p key={i} className="text-slate-300 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> {c.name}, {c.email}</p>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">First Name</label>
                  <Input value={childForm.firstName} onChange={e => { setChildForm({ ...childForm, firstName: e.target.value }); if (error) setError(''); }} data-testid="child-first-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 mb-1 block">Last Name</label>
                  <Input value={childForm.lastName} onChange={e => { setChildForm({ ...childForm, lastName: e.target.value }); if (error) setError(''); }} data-testid="child-last-name" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-300 mb-1 block">Date of Birth</label>
                <Input type="date" value={childForm.dob}
                  min={childDobMin} max={childDobMax} required
                  onChange={e => { setChildForm({ ...childForm, dob: e.target.value }); if (error) setError(''); }} data-testid="child-dob" />
                <p className="text-[10px] text-slate-300 mt-1">Must be 14-17 years old</p>
              </div>
              <div>
                <label className="text-xs text-slate-300 mb-1 block">Grade Level</label>
                <select value={childForm.grade} onChange={e => setChildForm({ ...childForm, grade: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="child-grade">
                  {['8th', '9th', '10th', '11th', '12th'].map(g => <option key={g} value={g}>{g} Grade</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-300 mb-1 block">Child's Email (for login)</label>
                <Input type="email" value={childForm.email} onChange={e => { setChildForm({ ...childForm, email: e.target.value }); if (error) setError(''); }} placeholder="child@email.com" data-testid="child-email" />
              </div>
              {error && <p className="text-red-400 text-sm bg-red-500/10 p-3 rounded-lg" data-testid="child-info-error">{error}</p>}
              <Button onClick={handleChildInfoNext} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 py-4 rounded-xl text-sm font-semibold" data-testid="child-next-btn">
                Continue to Consent Documents <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Child Consent Documents */}
        {step === 'child-consent' && (() => {
          const docType = DOC_TYPES[currentDocIndex];
          const template = templates[docType];
          const childFullName = `${childForm.firstName} ${childForm.lastName}`.trim();
          const DocIcon = DOC_ICONS[docType] || FileText;

          if (!template) return (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-300 text-sm">Loading documents...</p>
              <button onClick={loadTemplates} className="text-blue-400 hover:underline text-xs mt-2">Retry</button>
            </div>
          );

          return (
            <motion.div key={`${currentChild}-${currentDocIndex}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid={`consent-${docType}`}>
              {/* Progress badge */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300 text-sm">Signing for: <strong className="text-white">{childFullName}</strong></span>
                </div>
                <span className="text-xs text-slate-300">Doc {currentDocIndex + 1} / {DOC_TYPES.length}</span>
              </div>

              {/* Doc type pills */}
              <div className="flex gap-1.5 mb-3">
                {DOC_TYPES.map((dt, i) => {
                  const Icon = DOC_ICONS[dt] || FileText;
                  const active = i === currentDocIndex;
                  const done = i < currentDocIndex;
                  return (
                    <div key={dt} className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${
                      active ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                      done ? 'bg-emerald-500/10 text-emerald-400' :
                      'bg-slate-800 text-slate-300'
                    }`}>
                      {done ? <CheckCircle className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                      <span className="hidden sm:inline">{dt.split('_')[0]}</span>
                    </div>
                  );
                })}
              </div>

              <DocumentViewer
                title={`${template.title}, ${childFullName}`}
                sections={getEnhancedSections(template)}
                keyTerms={template.key_terms}
                signature={signature}
                setSignature={setSignature}
                onSign={handleSignDoc}
                signing={signing || loading}
              />

              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  onClick={() => {
                    setSignature('');
                    if (currentDocIndex > 0) {
                      setCurrentDocIndex(prev => prev - 1);
                    } else {
                      setStep('child-info');
                    }
                  }}
                  className="text-slate-300 hover:text-white text-xs flex items-center gap-1"
                  data-testid="consent-back-btn"
                >
                  <ChevronLeft className="w-3 h-3" />
                  {currentDocIndex > 0 ? `Back to previous document` : `Back to child info`}
                </button>
                <button
                  onClick={() => {
                    toast.success("Your account is active. Sign in any time to finish your child's setup.");
                    setTimeout(() => navigate('/parent/dashboard'), 600);
                  }}
                  className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                  data-testid="save-continue-later-btn"
                >
                  Save & continue later
                </button>
              </div>
            </motion.div>
          );
        })()}

        {/* Done */}
        {step === 'done' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6" data-testid="setup-complete">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
            <p className="text-slate-300 text-sm mb-5">Check your email for account details and login info.</p>

            {registeredChildren.length > 0 && (
              <div className="mb-5 p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-left">
                <p className="text-white text-sm font-medium mb-2">Children registered:</p>
                {registeredChildren.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-700/50 last:border-0">
                    <span className="text-slate-300 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> {c.name}</span>
                    <span className="text-slate-300 text-xs">{c.email}</span>
                  </div>
                ))}
                <p className="text-slate-300 text-xs mt-2">Login credentials sent to each child's email.</p>
              </div>
            )}

            <Button onClick={() => { window.location.href = regData?.role === 'parent' ? '/app/family' : '/app/dashboard'; }}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 py-5 rounded-xl text-sm font-semibold" data-testid="go-to-dashboard-btn">
              Go to My Dashboard <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
